// HEIC/HEIF -> JPEG conversion at upload time.
//
// Why this exists: iPhones shoot in HEIC by default. HEIC renders fine on
// Apple devices but NOT in Chrome, Firefox, Edge, or Windows — so a photo a
// field rep uploads from their iPhone shows as a broken/blank image for
// anyone in the office on a Windows machine. We convert to JPEG before upload
// so every stored image is universally viewable.
//
// Strategy (in order, each fails open to the next so an upload is NEVER
// blocked):
//   1. heic2any (libheif compiled to WASM) — decodes HEIC in ANY browser,
//      including non-Apple ones. Lazy-loaded from CDN only when a HEIC is
//      actually encountered, so it adds zero weight to the main bundle and
//      never runs for normal JPEG/PNG uploads.
//   2. Canvas re-encode — works on Apple devices that can natively decode
//      HEIC (covers the offline / CDN-unreachable case for the common
//      iPhone uploader).
//   3. Return the original file untouched — worst case we're back to today's
//      behavior for that one file; the user is never blocked.
//
// The companion backfill (one-time) already converted the HEIC files that
// were uploaded before this shipped.

// Pinned version so a future heic2any release can't silently change behavior.
// esm.sh ESM-ifies the (UMD) package and bundles its libheif dependency.
const HEIC2ANY_URL = "https://esm.sh/heic2any@0.0.4";

let _heic2anyPromise: Promise<((opts: unknown) => Promise<Blob | Blob[]>) | null> | null = null;

function loadHeic2any() {
  if (!_heic2anyPromise) {
    _heic2anyPromise = import(/* @vite-ignore */ HEIC2ANY_URL)
      .then((mod: unknown) => {
        const m = mod as { default?: unknown };
        const fn = (m?.default ?? mod) as ((opts: unknown) => Promise<Blob | Blob[]>) | undefined;
        return typeof fn === "function" ? fn : null;
      })
      .catch(() => null);
  }
  return _heic2anyPromise;
}

/** True if the file looks like HEIC/HEIF by mime type or extension. */
export function isHeic(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return (
    type.includes("heic") ||
    type.includes("heif") ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

function renameToJpg(original: File, blob: Blob): File {
  const newName = original.name.replace(/\.(heic|heif)$/i, ".jpg");
  // If the name had no .heic/.heif extension (mime-only detection), append .jpg.
  const finalName = newName === original.name ? `${original.name}.jpg` : newName;
  return new File([blob], finalName, {
    type: "image/jpeg",
    lastModified: original.lastModified,
  });
}

// Fallback: decode through an <img> + canvas and re-encode to JPEG. This only
// succeeds when the browser can natively decode the source (true for HEIC on
// modern Apple devices). On Chrome/Windows the <img> decode fails and we throw.
async function canvasReencode(file: File, quality: number): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("decode failed"));
      i.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * If `file` is HEIC/HEIF, return a JPEG File; otherwise return it unchanged.
 * Never throws — falls back to the original file so an upload is never blocked.
 */
export async function heicToJpeg(file: File, quality = 0.85): Promise<File> {
  if (!isHeic(file)) return file;

  // 1) heic2any (WASM) — works in every browser.
  try {
    const heic2any = await loadHeic2any();
    if (heic2any) {
      const out = await heic2any({ blob: file, toType: "image/jpeg", quality });
      const blob = Array.isArray(out) ? out[0] : out;
      if (blob && blob.size > 0) return renameToJpg(file, blob);
    }
  } catch {
    /* fall through to canvas */
  }

  // 2) Canvas re-encode — covers Apple devices when the CDN is unreachable.
  try {
    const blob = await canvasReencode(file, quality);
    if (blob && blob.size > 0) return renameToJpg(file, blob);
  } catch {
    /* fall through to original */
  }

  // 3) Give up gracefully — never block the upload.
  return file;
}
